import type { GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import { useEffect, useMemo, useState } from 'react';
import type { PaymentSchema } from '$api-clients/generated';
import type { PaymentRequest } from '$api-clients/hooks';
import { useIdentity, usePayment } from '$api-clients/hooks';
import {
  buildReportingSummaryPerformanceQuery,
  shouldUseYearlyReportingPeriod,
} from '../../helpers';
import { useAllTimeStartDate } from '../../ReportsConfiguration/useReportsConfiguration';
import { usePerformanceReportContext } from '../context/PerformanceReportContext';

export type TeamPerformanceAgentData =
  PaymentRequest<'/reporting/summary'>['Data']['results'][number] & {
    agent?: string;
    agentPhotoUrl?: string;
  };

export type TeamPerformanceTeamData =
  PaymentRequest<'/reporting/summary'>['Data']['results'][number] & {
    team?: string;
  };

type SortBy =
  PaymentSchema.paths['/reporting/summary']['get']['parameters']['query']['sortBy'];

const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_SORT_MODEL: GridSortModel = [
  { field: 'salesVolume', sort: 'desc' },
];

interface UseTeamPerformanceProps {
  viewReportAs: string;
  sortModel: GridSortModel;
  groupedBy: 'AGENT' | 'TEAM';
}

interface UseTeamPerformanceResult {
  rows: (TeamPerformanceAgentData | TeamPerformanceTeamData)[];
  loading: boolean;
  totalCount: number | undefined;
  rowCount: number | undefined;
  paginationModel: GridPaginationModel;
  setPaginationModel: React.Dispatch<React.SetStateAction<GridPaginationModel>>;
}

export {
  DEFAULT_PAGE_SIZE as TEAM_PERFORMANCE_DEFAULT_PAGE_SIZE,
  DEFAULT_SORT_MODEL as TEAM_PERFORMANCE_DEFAULT_SORT_MODEL,
};

export function useTeamPerformance({
  viewReportAs,
  sortModel,
  groupedBy,
}: UseTeamPerformanceProps): UseTeamPerformanceResult {
  const { filters } = usePerformanceReportContext();
  const allTimeStartDate = useAllTimeStartDate();
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  const isGroupedByAgent = groupedBy === 'AGENT';
  const useYearlyPeriod = shouldUseYearlyReportingPeriod(
    filters?.reportingPeriod,
  );

  // Reset to page 0 when filters change. Returns the same object reference when
  // already on page 0 so React bails out without a re-render, preventing a
  // double fetch.
  useEffect(() => {
    setPaginationModel((prev) =>
      prev.page === 0 ? prev : { ...prev, page: 0 },
    );
  }, [filters]);

  const query = useMemo(() => {
    const baseQuery = buildReportingSummaryPerformanceQuery(
      filters,
      viewReportAs,
      allTimeStartDate,
    );
    return {
      ...baseQuery,
      limit: paginationModel.pageSize,
      offset: paginationModel.page * paginationModel.pageSize,
      groupBy: [groupedBy],
      includePartiesWithoutTransactions: true,
      ...(useYearlyPeriod ? { period: 'YEARLY' as const } : undefined),
      sortBy: (sortModel[0]?.field as SortBy) || 'salesVolume',
      sortDirection: (sortModel[0]?.sort === 'asc' ? 'ASC' : 'DESC') as
        | 'ASC'
        | 'DESC',
    };
  }, [
    filters,
    viewReportAs,
    allTimeStartDate,
    paginationModel,
    groupedBy,
    useYearlyPeriod,
    sortModel,
  ]);

  const { data: summaryData, isLoading: isLoadingData } = usePayment(
    '/reporting/summary',
    { params: { query } },
  );

  const results = summaryData?.results ?? [];

  // Sort IDs for a stable SWR cache key regardless of result order.
  const userIds = useMemo(
    () =>
      isGroupedByAgent
        ? [
            ...new Set(
              results.flatMap((r) =>
                r.teamMemberIdentityId ? [r.teamMemberIdentityId] : [],
              ),
            ),
          ].sort()
        : [],
    [results, isGroupedByAgent],
  );
  const teamIds = useMemo(
    () =>
      !isGroupedByAgent
        ? [
            ...new Set(
              results.flatMap((r) =>
                r.teamIdentityId ? [r.teamIdentityId] : [],
              ),
            ),
          ].sort()
        : [],
    [results, isGroupedByAgent],
  );

  const { data: usersData, isLoading: isLoadingUsers } = useIdentity(
    '/users',
    userIds.length > 0
      ? { params: { query: { ids: userIds.join(',') } } }
      : null,
  );
  const { data: teamsData, isLoading: isLoadingTeams } = useIdentity(
    '/teams',
    teamIds.length > 0
      ? { params: { query: { ids: teamIds.join(',') } } }
      : null,
  );

  const usersById = useMemo(
    () => Object.fromEntries((usersData?.data ?? []).map((u) => [u.id, u])),
    [usersData],
  );
  const teamsById = useMemo(
    () => Object.fromEntries((teamsData?.data ?? []).map((t) => [t.id, t])),
    [teamsData],
  );

  const rows = useMemo(
    () =>
      results.map((r) => {
        if (isGroupedByAgent) {
          const user = r.teamMemberIdentityId
            ? usersById[r.teamMemberIdentityId]
            : undefined;
          return {
            ...r,
            agent: user?.displayName,
            agentPhotoUrl: user?.photoUrl ?? undefined,
          };
        }
        const team = r.teamIdentityId ? teamsById[r.teamIdentityId] : undefined;
        return {
          ...r,
          team: team ? (team.alias ?? team.legalName ?? '') : undefined,
        };
      }),
    [results, isGroupedByAgent, usersById, teamsById],
  );

  const isLoading = isLoadingData || isLoadingUsers || isLoadingTeams;

  return {
    rows,
    loading: isLoading,
    totalCount: summaryData?.totalCount,
    rowCount: results.length,
    paginationModel,
    setPaginationModel,
  };
}
